import { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import InventoryTab from '../../../components/tabs/InventoryTab';
import Button from '../../../components/base/Button';
import type { Domain } from '@shared/types/project.type';
import { getDomainDisplayName } from '@app/shared/utils/domainDisplay.utils';

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95vw', sm: '90vw', md: 720 },
  maxWidth: 720,
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: { xs: 2, sm: 3, md: 4 },
  borderRadius: 2,
  overflow: 'auto'
};

export interface MandatoryPolicyUploadModalProps {
  open: boolean;
  projectId: string;
  projectTitle: string;
  accountDomain: Domain | null;
  onContinue: () => void;
}

const MandatoryPolicyUploadModal = ({
  open,
  projectId,
  projectTitle,
  accountDomain,
  onContinue
}: MandatoryPolicyUploadModalProps) => {
  const [policyUploadCount, setPolicyUploadCount] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPolicyUploadCount(0);
      setUploadError(null);
    }
  }, [open, projectId]);

  const projectForUpload = useMemo(
    () =>
      accountDomain
        ? {
            projectId,
            domain: accountDomain
          }
        : undefined,
    [accountDomain, projectId]
  );

  const handlePolicyUploaded = useCallback((acceptedCount: number) => {
    const count = Number(acceptedCount);
    const increment = Number.isFinite(count) && count > 0 ? count : 1;
    setUploadError(null);
    setPolicyUploadCount(c => c + increment);
  }, []);

  const handlePolicyUploadError = useCallback((message: string) => {
    setUploadError(message || 'Upload failed');
  }, []);

  const canContinue = Boolean(accountDomain) && policyUploadCount > 0;

  const handleContinue = () => {
    if (!canContinue) return;
    onContinue();
  };

  return (
    <Modal open={open} disableEscapeKeyDown aria-labelledby="mandatory-policy-upload-title">
      <Box sx={modalStyle}>
        <Typography id="mandatory-policy-upload-title" variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
          Upload policy documents
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Project <strong>{projectTitle}</strong> was created. Upload at least one policy document (PDF or DOCX) to
          continue. This step is required.
          {accountDomain ? (
            <>
              {' '}
              Using your account domain: <strong>{getDomainDisplayName(accountDomain)}</strong>.
            </>
          ) : null}
        </Typography>

        {!accountDomain && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Your account domain is not set. Sign out and sign in again, or contact your administrator.
          </Alert>
        )}

        {accountDomain && projectForUpload && (
          <InventoryTab
            key={projectId}
            uploadMode="policy-only"
            projectForUpload={projectForUpload}
            onSuccessfulPolicyUpload={handlePolicyUploaded}
            onPolicyUploadError={handlePolicyUploadError}
          />
        )}

        {uploadError ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {uploadError}
          </Alert>
        ) : null}

        {!canContinue ? (
          <Typography variant="body2" sx={{ mt: 2, color: 'warning.main', fontWeight: 600 }}>
            No policy document uploaded yet.
          </Typography>
        ) : null}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="phoenix-primary" className="px-4" onClick={handleContinue} disabled={!canContinue}>
            Continue to project
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default MandatoryPolicyUploadModal;
